import * as React from "react";
import { Card } from "../../../bits/Card/Card";
import { useToasts } from "react-toast-notifications";
import { AclContext } from "../../../../../contexts/AclContext";
import {
    ClientStatus,
    DateFormats,
    IClient,
    IClientBooking,
} from "intake_shared/src/types";
import { Select } from "../../../bits/Select/Select";
import moment from "../../../../../utils/app_moment";
import store from "../../../../../store";
import { TextArea } from "../../../bits/TextArea/TextArea";
import styles from "./CompleteClientFlow.module.scss";
import { Button } from "../../../bits/Button/Button";
import { AlertDialog } from "../../../bits/AlertDialog/AlertDialog";
import { sendClientTerminationSms } from "../../../../../utils/queue_sms";
import { sendClientTerminationEmails } from "../../../../../utils/queue_email";
import _ from "lodash";
import { Dialog } from "../../../bits/Dialog/Dialog";
import { FormCheckbox } from "../../../bits/FormCheckbox/FormCheckbox";
import { buildSessionCancelChanges } from "../../../../helpers";
import Loader from "../../../bits/Loader/Loader";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { getAppointmentHeldStatusHistoryUpdate } from "../../../../appointment_held";

type CallerType = "clinician" | "admin";

const cancelSupervisorIntakeSessions = async (
    supervisorIntakeSessionsToCancel?: Array<{
        supervisorId: string;
        bookingId: string;
        clientId: string;
    }>
) => {
    if (
        supervisorIntakeSessionsToCancel &&
        supervisorIntakeSessionsToCancel.length > 0
    ) {
        for (const intakeSession of supervisorIntakeSessionsToCancel) {
            try {
                await store.removeIntakeSessionFromSupervisor(
                    intakeSession.supervisorId,
                    intakeSession.bookingId,
                    intakeSession.clientId
                );
            } catch (error) {
                console.error(
                    `Failed to remove intake session from supervisor ${intakeSession.supervisorId}:`,
                    error
                );
            }
        }
    }
};

export const CLIENT_CANCEL_REASONS = [
    "Asking Parents/Partner",
    "Cancelled - consent issue",
    "Client Cancelled",
    "N/A",
    "Non Responsive",
    "Spam",
    "Other",
    "Seeking Services Not Offered",
    "Shopping",
    "Thinking It Over",
    "Too Expensive",
    "Waitlist",
    "Want INN",
];

export const APPOINTMENT_CANCELLATION_REASONS = [
    "No Show",
    "Termination",
    "Cancelled",
    "Waitlist",
    "Transfer",
];

export const CASELOAD_CANCELLATION_REASONS = [
    ...APPOINTMENT_CANCELLATION_REASONS,
    "Session Expired",
];

export const COMPLETION_REASONS = Array.from(
    new Set([
        ...CLIENT_CANCEL_REASONS,
        ...CASELOAD_CANCELLATION_REASONS,
        "Pause limit exceeded",
    ])
);

enum AppoinmentCancellationOptions {
    ALL_APPOINTMENTS = "ALL_APPOINTMENTS",
}

interface ICompleteClientFlowProps {
    title?: string;
    showAppointmentsToCancel?: boolean;
    showCompletionReason?: boolean;
    showCompletionNotes?: boolean;
    showWaitListOption?: boolean;
    showOnlyWaitlist?: boolean;
    clientProp: IClient;
    appointmentToCancel?: IClientBooking;
    expectedCaller: CallerType;
    reasons?: string[];
    onSaveClient: (client: IClient, close?: boolean) => void;
    hideCard?: boolean; // New prop to hide card wrapper and close button
    onContinue?: () => void; // New prop to expose continue functionality
    completeBtnText?: string; // Custom button text
    disableInternalDialogs?: boolean;
    isBulkOperation?: boolean; // Indicates if this is a bulk operation
    bulkClients?: IClient[]; // All clients to process for bulk operations
    onBulkComplete?: () => void; // Callback after bulk operation completes
    viewFrom?: "appt_confirmed" | "general_view";
}
type Option = {
    label: string;
    value: string;
};

export interface CompleteClientFlowRef {
    handleSubmit: () => Promise<void>;
}

const CompleteClientFlow = React.forwardRef<
    CompleteClientFlowRef,
    ICompleteClientFlowProps
>(
    (
        {
            title,
            showAppointmentsToCancel = false,
            showCompletionReason = false,
            showCompletionNotes = false,
            showWaitListOption = false,
            showOnlyWaitlist = false,
            clientProp,
            appointmentToCancel,
            expectedCaller,
            reasons = CLIENT_CANCEL_REASONS,
            onSaveClient,
            hideCard = false,
            onContinue,
            completeBtnText = "Close client",
            disableInternalDialogs = false,
            isBulkOperation = false,
            bulkClients = [],
            onBulkComplete,
            viewFrom = "general_view",
        },
        ref
    ) => {
        const olc = React.useContext(CacheContext);
        const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
        const { addToast } = useToasts();
        const initialClient = isBulkOperation
            ? {
                  ...clientProp,
                  complete_notes: {
                      reason: "",
                      notes: "",
                  },
              }
            : clientProp;
        const [client, setClient] = React.useState(initialClient);
        const [sessionFields, setSessionFields] = React.useState({
            numberOfCancellations: 0,
            termination_message_requested: true,
            reason_for_termination_msg_skip: "",
        });

        const signatureEmail =
            client?.assigned_to || client?.secondary_assignee || "";
        const allEmailSignatures = olc?.emailSignatures || {};
        const signature = allEmailSignatures[signatureEmail as string];
        const [addedCompletionNote, setAddCompletionNote] = React.useState(
            isBulkOperation ? "" : clientProp.complete_notes?.notes || ""
        );

        const [selectedCompletionReason, setSelectedCompletionReason] =
            React.useState(
                isBulkOperation ? "" : clientProp.complete_notes?.reason || ""
            );

        const updateClient = React.useCallback(
            (
                label: keyof IClient,
                value:
                    | string
                    | Date
                    | ClientStatus
                    | boolean
                    | string[]
                    | {
                          reason: string;
                          notes?: string;
                          termination_message_requested?: boolean;
                          reason_for_termination_msg_skip?: string;
                      }
            ) => {
                setClient((prevClient) => {
                    return { ...prevClient, [label]: value };
                });
            },
            []
        );

        const [selectedAppointmentId, setSelectedAppointmentId] = React.useState(
            appointmentToCancel?.id || AppoinmentCancellationOptions.ALL_APPOINTMENTS
        );
        const [showDialog, setShowDialog] = React.useState(false);
        const [showCloseClientDialog, setCloseClientDialog] = React.useState(false);
        const [showLoader, setShowLoader] = React.useState(false);

        React.useEffect(() => {
            if (isBulkOperation) {
                setClient((prevClient) => ({
                    ..._.merge(prevClient, clientProp),
                    complete_notes: prevClient.complete_notes || {
                        reason: "",
                        notes: "",
                    },
                }));
            } else {
                setClient((prevClient) => _.merge(prevClient, clientProp));
            }
        }, [clientProp, isBulkOperation]);

        // get Clinician details for appointment to cancel
        const getClinicians = React.useCallback(async () => {
            const clinicianIds: string[] = [];
            if (selectedAppointmentId !== "ALL_APPOINTMENTS") {
                const selectedAppointment = client.confirmed_bookings.find(
                    (b) => b.id === selectedAppointmentId
                ) as IClientBooking;
                clinicianIds.push(selectedAppointment?.clinician_id);
            } else if (selectedAppointmentId === "ALL_APPOINTMENTS") {
                clinicianIds.push(
                    ...client.confirmed_bookings.map((c) => c.clinician_id)
                );
            }
            const clinicians = await store.fetchCliniciansByIds(clinicianIds);
            return clinicians;
        }, [selectedAppointmentId, client.confirmed_bookings]);

        // this is the function to continue to the next action which is the popup
        const handleContinue = React.useCallback(async () => {
            if (!loggedInUserEmail) {
                addToast("Error determining logged in user", {
                    appearance: "error",
                });
                return;
            }
            if (!showAppointmentsToCancel) {
                if (!disableInternalDialogs) {
                    return setCloseClientDialog(true);
                }
            }
            const clinicians = await getClinicians();
            try {
                const { numberOfCancellations = 0 } = buildSessionCancelChanges({
                    caller: expectedCaller as CallerType,
                    cancellerId: loggedInUserEmail as string,
                    client,
                    clinicians,
                    selectedAppointmentId,
                    sessionFields,
                });
                setSessionFields({
                    ...sessionFields,
                    numberOfCancellations: numberOfCancellations,
                });
                if (!disableInternalDialogs) {
                    setShowDialog(true);
                } else {
                    if (onContinue) {
                        onContinue();
                    }
                }
            } catch (err) {
                console.error(err);
            }
        }, [
            loggedInUserEmail,
            addToast,
            showAppointmentsToCancel,
            getClinicians,
            expectedCaller,
            client,
            selectedAppointmentId,
            sessionFields,
            setCloseClientDialog,
            setShowDialog,
            disableInternalDialogs,
            onContinue,
        ]);

        // function to finalize action and send message and email
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const handleConfirm = async () => {
            if (!loggedInUserEmail) {
                addToast("Error determining logged in user", {
                    appearance: "error",
                });
                return;
            }
            if (!showAppointmentsToCancel) {
                // For bulk operations, process all clients here
                setShowLoader(true);
                if (isBulkOperation && bulkClients.length > 0) {
                    try {
                        for (const clientToClose of bulkClients) {
                            let status: ClientStatus | undefined;

                            if (clientToClose.confirmed_bookings?.length > 0) {
                                status = ClientStatus.APPT_CONFIRMED;
                            } else if (clientToClose.held_bookings?.length > 0) {
                                status = ClientStatus.APPT_HELD;
                            } else {
                                status = ClientStatus.COMPLETED;
                            }

                            if (status) {
                                const clinicians = [];
                                const appointmentId = "";
                                const {
                                    updatedClientChangesToSave,
                                    supervisorIntakeSessionsToCancel,
                                } = buildSessionCancelChanges({
                                    caller: expectedCaller as CallerType,
                                    cancellerId: loggedInUserEmail as string,
                                    client: clientToClose,
                                    clinicians,
                                    selectedAppointmentId: appointmentId,
                                    sessionFields,
                                });

                                const clientChanges = {
                                    ...updatedClientChangesToSave,
                                    status,
                                    complete_notes: {
                                        reason: client.complete_notes?.reason || "",
                                        notes: addedCompletionNote || "",
                                    },
                                    archived_on:
                                        status === ClientStatus.COMPLETED
                                            ? new Date()
                                            : null,
                                };
                                const statusHistory =
                                    getAppointmentHeldStatusHistoryUpdate(
                                        clientToClose,
                                        status
                                    );
                                if (statusHistory) {
                                    Object.assign(clientChanges, {
                                        status_history: statusHistory,
                                    });
                                }

                                await store.updateClientData({
                                    client_id: clientToClose.id,
                                    changes: clientChanges,

                                    last_updated_by: loggedInUserEmail as string,
                                });

                                await cancelSupervisorIntakeSessions(
                                    supervisorIntakeSessionsToCancel
                                );
                            }
                        }

                        setShowLoader(false);
                        setCloseClientDialog(false);
                        setShowDialog(false);
                        addToast(
                            `Successfully closed ${bulkClients.length} client(s)`,
                            {
                                appearance: "success",
                            }
                        );
                        if (onBulkComplete) {
                            onBulkComplete();
                        }
                        return;
                    } catch (error) {
                        setShowLoader(false);
                        addToast("Error closing clients", {
                            appearance: "error",
                        });
                        console.error(error);
                        return;
                    }
                }

                let status: ClientStatus | undefined;

                if (client.confirmed_bookings.length > 0) {
                    // If there are confirmed bookings, set status to confirmed (priority)
                    status = ClientStatus.APPT_CONFIRMED;
                } else if (client.held_bookings.length > 0) {
                    // If there are no confirmed bookings but there are held bookings, set status to held
                    status = ClientStatus.APPT_HELD;
                } else {
                    // If there are no confirmed or held bookings, set status to completed
                    status = ClientStatus.COMPLETED;
                }

                const clinicians = [];
                const appointmentId = "";
                const {
                    updatedClientChangesToSave,
                    supervisorIntakeSessionsToCancel,
                } = buildSessionCancelChanges({
                    caller: expectedCaller as CallerType,
                    cancellerId: loggedInUserEmail as string,
                    client,
                    clinicians,
                    selectedAppointmentId: appointmentId,
                    sessionFields,
                });

                if (status) {
                    const clientChanges = {
                        ...updatedClientChangesToSave,
                        status,
                        complete_notes: {
                            reason: client.complete_notes?.reason || "",
                            notes: addedCompletionNote || "",
                        },
                        archived_on:
                            status === ClientStatus.COMPLETED ? new Date() : null,
                    };
                    const statusHistory = getAppointmentHeldStatusHistoryUpdate(
                        client,
                        status
                    );
                    if (statusHistory) {
                        Object.assign(clientChanges, {
                            status_history: statusHistory,
                        });
                    }

                    await store.updateClientData({
                        client_id: client.id,
                        changes: clientChanges,
                        last_updated_by: loggedInUserEmail as string,
                    });

                    await cancelSupervisorIntakeSessions(
                        supervisorIntakeSessionsToCancel
                    );
                    setShowLoader(false);
                    setCloseClientDialog(false);
                    setShowDialog(false);
                    onSaveClient(client, true);
                    addToast("Client updated successfully", {
                        appearance: "success",
                    });
                    return;
                }
            }

            const clinicians = await getClinicians();
            try {
                setShowLoader(true);
                const {
                    canCancel,
                    numberOfCancellations = 0,
                    updatedClientChangesToSave,
                    updatedClininicianChangesToSave,
                    supervisorIntakeSessionsToCancel,
                } = buildSessionCancelChanges({
                    caller: expectedCaller as CallerType,
                    cancellerId: loggedInUserEmail as string,
                    client,
                    clinicians,
                    selectedAppointmentId,
                    sessionFields,
                });

                // fields to send when the reason is termination
                const updatedChangesForTerminationReason = {
                    ...updatedClientChangesToSave,
                    complete_notes: {
                        notes: client.complete_notes?.notes || "",
                        reason: client.complete_notes?.reason || "",
                        termination_message_requested:
                            sessionFields.termination_message_requested,
                        reason_for_termination_msg_skip:
                            sessionFields.reason_for_termination_msg_skip || "",
                    },
                    archived_on:
                        updatedClientChangesToSave?.status === ClientStatus.COMPLETED
                            ? new Date()
                            : null,
                };

                // fields to send when the reason is not termination
                const updatedChangesForNonTerminationReason = {
                    ...updatedClientChangesToSave,
                    complete_notes: {
                        notes: client.complete_notes?.notes || "",
                        reason: client.complete_notes?.reason || "",
                    },
                    archived_on:
                        updatedClientChangesToSave?.status === ClientStatus.COMPLETED
                            ? new Date()
                            : null,
                };

                if (!canCancel) {
                    setShowLoader(false);
                    addToast(
                        "Sorry, you can't cancel the appointment right now. It might be because you don't have enough permission to do so. Please get in touch with the clinician or an admin for help. And if you're the owner of the appointment or an admin, contact tech support.",
                        {
                            appearance: "error",
                            autoDismissTimeout: 15000,
                        }
                    );
                    return;
                }

                if (canCancel && numberOfCancellations >= 1) {
                    if (expectedCaller === "clinician") {
                        const remainingBookingIds = new Set(
                            (
                                updatedClientChangesToSave?.confirmed_bookings || []
                            ).map((booking) => booking.id)
                        );
                        const cancelledBookings = client.confirmed_bookings.filter(
                            (booking) => !remainingBookingIds.has(booking.id)
                        );
                        for (const booking of cancelledBookings) {
                            await store.cancelOwnConfirmedAppointment({
                                clinicianId: booking.clinician_id,
                                clientId: booking.client_id,
                                appointmentId: booking.id,
                            });
                        }
                    } else {
                        for (const clinicianId in updatedClininicianChangesToSave) {
                            await store.updateClinicianData({
                                clinician_id: clinicianId,
                                confirmed_bookings:
                                    updatedClininicianChangesToSave[clinicianId]
                                        .confirmed,
                            });
                        }
                    }
                }
                await store.updateClientData({
                    client_id: client.id,
                    changes:
                        client.complete_notes?.reason === "Termination"
                            ? updatedChangesForTerminationReason
                            : updatedChangesForNonTerminationReason,
                    last_updated_by: loggedInUserEmail as string,
                });

                await cancelSupervisorIntakeSessions(
                    supervisorIntakeSessionsToCancel
                );

                // get the appointment
                const selectedAppointment = client.confirmed_bookings.find(
                    (b) => b.id === selectedAppointmentId
                ) as IClientBooking;
                onSaveClient(client, true);

                // only send email and text message when reason is termination and termination message is requested and client consented
                if (
                    client.complete_notes?.reason === "Termination" &&
                    sessionFields.termination_message_requested
                ) {
                    if (client.consent_to_sms === "Yes") {
                        const defaultMessage = `Hi ${client.first_name}, We're sorry to see you go! We are grateful for the opportunity to partner with you and wish you all the best! If you have any feedback you'd like to share, you may do so here https://humantold.com/rate-our-service/`;
                        // Define the link and the message text for sms
                        await sendClientTerminationSms(client, defaultMessage);
                    }
                    await sendClientTerminationEmails(
                        client,
                        selectedAppointment,
                        signature
                    );
                }

                setShowLoader(false);
                setShowDialog(false);
                addToast("Client appointment(s) have been cancelled", {
                    appearance: "success",
                });
            } catch (err) {
                console.error(err);
                addToast("Error cancelling appointments", {
                    appearance: "error",
                });
                setShowLoader(false);
            }
        };

        // Direct submit function for use in dialogs (doesn't show internal confirmation dialogs)
        const handleSubmit = React.useCallback(async () => {
            if (!loggedInUserEmail) {
                addToast("Error determining logged in user", {
                    appearance: "error",
                });
                return;
            }

            if (showCompletionReason && !client.complete_notes?.reason) {
                addToast("Please select a reason", {
                    appearance: "error",
                });
                return;
            }

            // Validate completion notes if required
            if (showCompletionNotes && client.complete_notes?.reason) {
                if (
                    client.complete_notes.reason === "Spam" &&
                    !addedCompletionNote
                ) {
                    addToast("Please provide a reason for marking this as spam", {
                        appearance: "error",
                    });
                    return;
                }
                if (
                    client.complete_notes.reason !== "Spam" &&
                    (client.status === ClientStatus.APPT_CONFIRMED ||
                        bulkClients.some(
                            (c) => c.status === ClientStatus.APPT_CONFIRMED
                        )) &&
                    addedCompletionNote.length < 50
                ) {
                    addToast(
                        "Please provide a completion note of at least 50 characters",
                        {
                            appearance: "error",
                        }
                    );
                    return;
                }
            }

            // If reason is termination, show the termination confirmation dialog
            if (client.complete_notes?.reason === "Termination") {
                setShowDialog(true);
                return;
            }

            // Call handleConfirm directly for non-termination cases
            await handleConfirm();
        }, [
            loggedInUserEmail,
            addToast,
            showCompletionReason,
            showCompletionNotes,
            client.complete_notes?.reason,
            client.status,
            addedCompletionNote,
            bulkClients,
            handleConfirm,
            setShowDialog,
        ]);

        React.useImperativeHandle(ref, () => ({
            handleSubmit,
        }));

        const isSubmitBtnDisabled = () => {
            // If reason is required but not selected, disable the button
            if (showCompletionReason && !selectedCompletionReason) {
                return true;
            }

            if (selectedCompletionReason === "Spam" && !addedCompletionNote) {
                return true;
            }

            if (
                selectedCompletionReason !== "Spam" &&
                (client.status === ClientStatus.APPT_CONFIRMED ||
                    bulkClients.some(
                        (c) => c.status === ClientStatus.APPT_CONFIRMED
                    )) &&
                addedCompletionNote.length < 50
            ) {
                return true;
            }

            return false;
        };

        const completionNoteErrorText = () => {
            if (selectedCompletionReason) {
                if (selectedCompletionReason === "Spam" && !addedCompletionNote) {
                    return "Please provide a reason for marking this as spam";
                }
                if (
                    selectedCompletionReason !== "Spam" &&
                    (client.status === ClientStatus.APPT_CONFIRMED ||
                        bulkClients.some(
                            (c) => c.status === ClientStatus.APPT_CONFIRMED
                        )) &&
                    addedCompletionNote.length < 50
                ) {
                    return "Please provide a completion note of at least 50 characters";
                }
            }
            return "";
        };

        const appointmentOptions: Option[] = React.useMemo(
            () => [
                {
                    label: "All appointments and close client",
                    value: "all_appointments_and_close_client",
                },
                ...client.confirmed_bookings.map((b) => ({
                    label: `${b.clinician_display_name} / ${
                        b.requested_service
                    } / ${moment(b.start).format(DateFormats.DAY_HOUR_FORMAT)}`,
                    value: b.id,
                })),
            ],
            [client.confirmed_bookings]
        );

        const completionReasonOptions: Option[] = [
            {
                label: "Select",
                value: "",
            },
            ...reasons.map((r) => ({
                label: r,
                value: r,
            })),
        ];

        const isBulk = isBulkOperation && bulkClients.length > 1;
        const clientCount = isBulk ? bulkClients.length : 1;
        const clientText = clientCount === 1 ? "client" : "clients";
        const thisTheseText =
            clientCount === 1 ? "this client" : `these ${clientCount} clients`;
        const appointmentText =
            clientCount === 1 ? "an existing appointment" : "existing appointments";

        return (
            <div className={styles.closeClientTab}>
                <Loader show={showLoader} />
                <AlertDialog
                    open={showCloseClientDialog}
                    onClose={() => setCloseClientDialog(false)}
                    title={`Close ${clientCount} ${clientText}`}
                    saveText="Confirm"
                    description={
                        <>
                            <p>
                                Are you sure you want to close {thisTheseText}? If
                                they have {appointmentText} they will be moved back
                                to Appoinment Confirmed.
                            </p>
                            <p>Otherwise they will be moved to Completed.</p>
                        </>
                    }
                    onSaveClick={handleConfirm}
                />
                <Dialog
                    open={showDialog}
                    onClose={() => setShowDialog(false)}
                    title={
                        client.complete_notes?.reason === "Termination"
                            ? "Terminate Session"
                            : `Cancel ${sessionFields?.numberOfCancellations} ${
                                  sessionFields?.numberOfCancellations > 1
                                      ? "sessions"
                                      : "session"
                              }?`
                    }
                    saveText="Confirm"
                    onSave={handleConfirm}
                    isCancelBtnDisabled={
                        !sessionFields?.termination_message_requested &&
                        sessionFields?.reason_for_termination_msg_skip === ""
                    }
                    onCancel={() => {
                        setShowDialog(false);
                    }}
                >
                    {client.complete_notes?.reason === "Termination" ? (
                        <div>
                            <h6>Are you sure you want to cancel this session?</h6>
                            <p>
                                This action cannot be reversed. Humantold will reach
                                out to this client to inform them of the termination
                                via email and SMS.
                            </p>
                            <p>
                                If you do not want this to happen. Uncheck the box
                                below and write a reason.
                            </p>
                            <form className="v2-form">
                                <FormCheckbox
                                    label="Notify the client of the termination"
                                    checked={
                                        sessionFields?.termination_message_requested
                                    }
                                    onChange={() => {
                                        setSessionFields((prev) => {
                                            return {
                                                ...prev,
                                                termination_message_requested:
                                                    !prev.termination_message_requested,
                                                reason_for_termination_msg_skip: "",
                                            };
                                        });
                                    }}
                                />
                                {sessionFields.termination_message_requested &&
                                    (client.consent_to_sms === "No" ||
                                        !client.consent_to_sms) && (
                                        <div className={styles.termiationMessage}>
                                            This client has opted out of SMS
                                            communication and will only receive email
                                            notifications
                                        </div>
                                    )}
                                {!sessionFields?.termination_message_requested && (
                                    <>
                                        <TextArea
                                            required
                                            value={
                                                sessionFields?.reason_for_termination_msg_skip ||
                                                ""
                                            }
                                            onChange={(e) => {
                                                setSessionFields({
                                                    ...sessionFields,
                                                    reason_for_termination_msg_skip:
                                                        e.target.value,
                                                });
                                            }}
                                            className={
                                                sessionFields?.reason_for_termination_msg_skip
                                                    ? ""
                                                    : styles.invalid
                                            }
                                        />
                                        <p>
                                            {" "}
                                            <span className={styles.asterisk}>
                                                *{" "}
                                            </span>
                                            Provide a reason for not notifying the
                                            client
                                        </p>
                                    </>
                                )}
                            </form>
                        </div>
                    ) : (
                        `Are you sure you want to
                    cancel ${sessionFields?.numberOfCancellations} ${
                            sessionFields?.numberOfCancellations > 1
                                ? "sessions"
                                : "session"
                        }?
                    This action cannot be
                    reversed.`
                    )}
                </Dialog>
                {!hideCard && (
                    <Card>
                        <p className={styles.closeClientTabTitle}>Close client</p>
                        <form className="v2-form">
                            {showAppointmentsToCancel && (
                                <Select
                                    label="Select appointment to cancel"
                                    required
                                    isDisabled={
                                        Boolean(appointmentToCancel) ||
                                        expectedCaller === "clinician"
                                    }
                                    defaultValue={
                                        appointmentOptions.find(
                                            (o) => o.value === selectedAppointmentId
                                        ) || {
                                            label: "All appointments and close client",
                                            value: "all_appointments_and_close_client",
                                        }
                                    }
                                    options={appointmentOptions}
                                    onChange={(value) => {
                                        setSelectedAppointmentId(
                                            (value as Option).value
                                        );
                                    }}
                                />
                            )}
                            {showCompletionReason && (
                                <Select
                                    label="Select reason"
                                    hasError={!selectedCompletionReason}
                                    errorText={
                                        !selectedCompletionReason
                                            ? "Please select a reason"
                                            : ""
                                    }
                                    defaultValue={completionReasonOptions.find(
                                        (o) =>
                                            o.value === client.complete_notes?.reason
                                    )}
                                    options={completionReasonOptions}
                                    onChange={(value) => {
                                        updateClient("complete_notes", {
                                            ...(client.complete_notes || {}),
                                            reason: (value as Option).value,
                                        });
                                        setSelectedCompletionReason(
                                            (value as Option).value
                                        );
                                    }}
                                />
                            )}
                            {showCompletionNotes && (
                                <>
                                    <TextArea
                                        label="Completion notes"
                                        required
                                        value={addedCompletionNote}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setAddCompletionNote(newValue);
                                            updateClient("complete_notes", {
                                                ...(client.complete_notes || {}),
                                                notes: newValue,
                                            });
                                        }}
                                        hasError={!!completionNoteErrorText()}
                                        errorText={completionNoteErrorText()}
                                    />
                                </>
                            )}
                            <div className={styles.closeClientButton}>
                                <Button
                                    onClick={() => {
                                        if (
                                            client.complete_notes?.reason === "Spam"
                                        ) {
                                            handleConfirm();
                                        } else {
                                            handleContinue();
                                        }
                                    }}
                                    disabled={isSubmitBtnDisabled()}
                                    variant="danger"
                                >
                                    {completeBtnText}
                                </Button>
                            </div>
                        </form>
                    </Card>
                )}
                {hideCard && (
                    <form className="v2-form">
                        {showCompletionReason && (
                            <Select
                                label="Select reason"
                                hasError={!selectedCompletionReason}
                                errorText={
                                    !selectedCompletionReason
                                        ? "Please select a reason"
                                        : ""
                                }
                                defaultValue={completionReasonOptions.find(
                                    (o) => o.value === client.complete_notes?.reason
                                )}
                                options={completionReasonOptions}
                                onChange={(value) => {
                                    updateClient("complete_notes", {
                                        ...(client.complete_notes || {}),
                                        reason: (value as Option).value,
                                    });
                                    setSelectedCompletionReason(
                                        (value as Option).value
                                    );
                                }}
                                isLongListInDialog
                            />
                        )}
                        {showCompletionNotes && (
                            <>
                                <TextArea
                                    label="Completion notes"
                                    isRequired
                                    value={addedCompletionNote}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setAddCompletionNote(newValue);
                                        updateClient("complete_notes", {
                                            ...(client.complete_notes || {}),
                                            notes: newValue,
                                        });
                                    }}
                                    hasError={!!completionNoteErrorText()}
                                    errorText={completionNoteErrorText()}
                                />
                            </>
                        )}
                        {viewFrom === "appt_confirmed" ? null : (
                            <div className={styles.closeClientButton}>
                                <Button
                                    onClick={() => {
                                        if (
                                            client.complete_notes?.reason === "Spam"
                                        ) {
                                            handleConfirm();
                                        } else {
                                            handleContinue();
                                        }
                                    }}
                                    variant="danger"
                                    disabled={isSubmitBtnDisabled()}
                                >
                                    {completeBtnText}
                                </Button>
                            </div>
                        )}
                    </form>
                )}
            </div>
        );
    }
);

export default CompleteClientFlow;
